import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chartjs } from 'app/trello/chartjs.imports';
import { GroupedMap2D } from 'app/trello/models/board-summary';
import { ChartableData, LabelledValues } from 'app/trello/models/charts';
import { ReadableSecondsPipe } from 'app/trello/pipes/readable-seconds.pipe';
import { ToSecondsPipe } from 'app/trello/pipes/to-seconds.pipe';
import { Templating } from 'app/trello/templating.imports';
import { StatisticsExtractor } from 'app/trello/utility/statistics-extractor';
import { StructureTransformer } from 'app/trello/utility/structure-transformer';
import { array } from 'app/trello/utility/utility-functions';
import { Chart, ChartOptions, ChartType, Color, LegendOptions, TooltipOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Chartjs,
    Templating,
    ContextMenuModule,
    DialogModule,
    ReadableSecondsPipe,
    ToSecondsPipe,
  ],
  providers: [ReadableSecondsPipe],
  selector: 'app-durations-chart-with-table',
  styleUrls: ['./durations-chart-with-table.component.css'],
  templateUrl: './durations-chart-with-table.component.html',
})
export class DurationsChartWithTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input({required: true}) chartData!:
    ChartableData<LabelledValues, GroupedMap2D<number>> |
    ChartableData<LabelledValues, GroupedMap2D<number>>[];

  @Input() chartContainer: HTMLElement | undefined = undefined;

  @Input() tableTitle: string | undefined;
  @Input() tableGroupColumnLabel: string = 'Group';

  @Input() valueFormat: 'number' | 'human-readable-time' | 'custom' = 'human-readable-time';
  @Input() valueUnit: 'number' | 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'days' = 'milliseconds';

  @Input() valueFormatter: (value: any) => string = (value: any) => String(value);

  @ViewChild('canvasContainer') protected canvasContainer!: ElementRef;
  @ViewChild(BaseChartDirective) private chartDirective!: BaseChartDirective;

  private chartContainerResizeObserver: ResizeObserver | undefined;

  private get chartInstance(): Chart | undefined {
    return this.chartDirective?.chart;
  }

  private initialized: boolean = false;
  private initializedView: boolean = false;

  protected readonly canvasContextMenuItems = [
    {
      label: 'View Data',
      command: () => {
        this.showDialog();
      }
    }
  ];

  protected readonly chartType: ChartType = 'bar';
  protected readonly chartLegend: boolean = true;
  protected chartOptions!: ChartOptions;

  protected mappedChartAxisLabels: string[] = [];

  protected mappedChartDatasets: {
    data: number[];
    label: string | undefined;
    backgroundColor?: Color | Color[];
    borderColor?: Color | Color[];
    fill?: boolean;
  }[] = [];

  protected mappedChartDatasetsReady: boolean = false;

  protected mappedTableValues: {
    group: string;
    count: number;
    sum: number;
    min: number;
    max: number;
    avg: number;
    individual?: {
      [tableValueIndex: string]: {
        group: string;
        count: number;
        sum: number;
        min: number;
        max: number;
        avg: number;
      }
    },
  }[] = [];

  protected mappedTableValuesReady: boolean = false;

  protected multipleDataPoints: boolean = false;
  protected tableDataAvailable: boolean = false;

  protected tableDialogOpen: boolean = false;

  protected readonly array = array;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly readableSecondsPipe: ReadableSecondsPipe,
    private readonly statisticsExtractor: StatisticsExtractor,
    private readonly structureTransformer: StructureTransformer,
  ) {
  }

  public ngOnInit(): void {

    this.initialized = true;

    this.prepareData();
  }

  public ngAfterViewInit(): void {

    this.initializedView = true;

    this.keepCanvasSizeSameAsChartContainer();

    this.prepareChartOptions();

    this.changeDetector.detectChanges();

    if (this.chartInstance) {
      this.chartDirective.update();
    }
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartContainer']) {
      this.keepCanvasSizeSameAsChartContainer();
    }

    if (changes['chartData']) {
      this.prepareData();

      if (this.chartInstance) {
        this.chartDirective.update();
      }
    }

    if (changes['chartLegend']
      || changes['valueFormat']
      || changes['valueUnit']) {

      this.prepareChartOptions();

      if (this.chartInstance) {
        this.chartDirective.update();
      }
    }
  }

  public ngOnDestroy(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }
  }

  protected showDialog(): void {

    this.tableDialogOpen = true;

    this.changeDetector.detectChanges();
  }

  private formatValue(
    value: any,
  ): string {

    if (this.valueFormat === 'custom') {
      return this.valueFormatter(value);
    }

    if (this.valueFormat === 'human-readable-time') {

      const seconds = Number(value) / 1000;

      return this.readableSecondsPipe.transform(seconds, 1) || '';
    }

    return String(value);
  }

  private keepCanvasSizeSameAsChartContainer(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }

    if (!this.chartContainer
      || !this.initializedView) {
      return;
    }

    this.chartContainerResizeObserver =
      new ResizeObserver((entries) => {

        for (const entry of entries) {

          if (entry.target === this.chartContainer) {

            let {width, height} = entry.contentRect;

            width = Math.round(width);
            height = Math.round(height);

            if (this.canvasContainer) {

              this.canvasContainer.nativeElement.style.width = `${width}px`;
              this.canvasContainer.nativeElement.style.height = `${height}px`;
            }
          }
        }
      });

    this.chartContainerResizeObserver.observe(this.chartContainer);
  }

  private prepareChartOptions(): void {

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'x',
      interaction: {
        axis: 'x',
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: {
          type: 'category',
          display: true,
          title: {
            display: false,
            text: '',
          },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 20,
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: false,
            text: '',
          },
          ticks: {
            precision: 0,
            callback: (value: string | number) => this.formatValue(Number(value)),
          },
        },
      },
      plugins: {
        legend: {
          display: true,
        } as LegendOptions<'bar'>,
        tooltip: {
          position: 'nearest',
          callbacks: {
            label: (context) => {

              const label = context.dataset.label || '';
              const value = context.parsed.y;

              return `${label}: ${this.formatValue(value)}`;
            },
          },
        } as TooltipOptions<'bar'>,
        noTooltipColorBoxBorders: {
          enabled: true,
        },
        timeBasedTicks: {
          valueAxis: 'y',
          valueUnit: this.valueUnit,
        },
      } as any,
    };
  }

  private prepareData(): void {

    this.prepareChartDatasets();
    this.prepareTableValues();

    this.multipleDataPoints =
      Array.isArray(this.chartData)
      && this.chartData.length > 1;
  }

  private prepareChartDatasets(): void {

    if (!this.initialized) {
      return;
    }

    const chartDataPoints =
      Array.isArray(this.chartData)
        ? this.chartData
        : [this.chartData as ChartableData<LabelledValues, unknown>];

    if (chartDataPoints.length === 0) {

      this.mappedChartAxisLabels = [];

      this.mappedChartDatasets = [];
      this.mappedChartDatasetsReady = true;

      return;
    }

    const firstDatasetLabels = chartDataPoints[0].chartData.labels || [];

    const allLabelsSame =
      chartDataPoints
        .map((chartDataPoint) => chartDataPoint.chartData)
        .map((labelledValues) => labelledValues.labels || [])
        .every((labels: string[]) => {

          if (labels.length !== firstDatasetLabels.length) {
            return false;
          }

          return labels.every((label, index) => label === firstDatasetLabels[index]);
        });

    if (allLabelsSame) {

      this.mappedChartAxisLabels = [...firstDatasetLabels];

    } else {

      const uniqueLabels = new Set<string>();

      chartDataPoints
        .map((chartDataPoint) => chartDataPoint.chartData)
        .map((labelledValues) => labelledValues.labels)
        .forEach((labels: string[]) => {

          if (labels && Array.isArray(labels)) {
            labels.forEach((label: string) => uniqueLabels.add(label));
          }
        });

      this.mappedChartAxisLabels = Array.from(uniqueLabels).sort();
    }

    this.mappedChartDatasets =
      chartDataPoints
        .map((chartDataPoint: ChartableData<LabelledValues, unknown>) => {

          const labelledValues: LabelledValues = chartDataPoint.chartData;

          const labels = labelledValues.labels;
          const values = labelledValues.values;

          let alignedData: number[];

          if (allLabelsSame) {

            alignedData =
              values && values.length === this.mappedChartAxisLabels.length
                ? [...values]
                : this.mappedChartAxisLabels.map((_: string, index: number) => values ? values[index] || 0 : 0);

          } else {

            const valueByLabel = new Map<string, number>();

            if (labels && values && labels.length === values.length) {

              labels.forEach((label: string, index: number) => {
                valueByLabel.set(label, values![index]);
              });
            }

            alignedData = this.mappedChartAxisLabels.map((label: string): number => valueByLabel.get(label) || 0);
          }

          return this.toChartDataset(
            alignedData,
            chartDataPoint.chartLabel,
            chartDataPoint.primaryColor,
            chartDataPoint.secondaryColor,
            chartDataPoint.fill
          );
        });

    this.mappedChartDatasetsReady = true;
  }

  private prepareTableValues(): void {

    if (!this.initialized) {
      return;
    }

    if (!Array.isArray(this.chartData)) {

      this.mappedTableValues = this.statisticsExtractor.aggregateNumericStatsByGroup(this.chartData.tableData);

      this.mappedTableValuesReady = true;

      this.tableDataAvailable = this.mappedTableValues.length > 0;

      return;
    }

    const maxAccumulationPerDay = 86400000; // 24 hours in milliseconds

    const tableDataPoints: GroupedMap2D<number>[] =
      this.chartData
        .map((chartDataPoint) => chartDataPoint.tableData)
        .filter((tableDataPoint) => tableDataPoint !== undefined);

    const mergedTableDataPoints = this.structureTransformer.mergeInnerNumbers(true, maxAccumulationPerDay, ...tableDataPoints);

    const mergedDurations = this.statisticsExtractor.aggregateNumericStatsByGroup(mergedTableDataPoints) as any[];

    for (let i = 0; i < tableDataPoints.length; i++) {

      const unmergedDurationsByDate = new Map<
        string,
        {
          group: string;
          count: number;
          sum: number;
          min: number;
          max: number;
          avg: number;
        }
      >();

      const unmergedDurations = this.statisticsExtractor.aggregateNumericStatsByGroup(tableDataPoints[i]);

      unmergedDurations.forEach((unmergedDuration) =>
        unmergedDurationsByDate.set(unmergedDuration.group, unmergedDuration),
      );

      for (let j = 0; j < mergedDurations.length; j++) {

        const date = mergedDurations[j]['group'];

        const unmergedDurationsForDate = unmergedDurationsByDate.get(date);

        if (!unmergedDurationsForDate) {
          continue;
        }

        if (!mergedDurations[j]['individual']) {
          mergedDurations[j]['individual'] = {};
        }

        mergedDurations[j]['individual'][i] = unmergedDurationsForDate;
      }
    }

    this.mappedTableValues = mergedDurations;
    this.mappedTableValuesReady = true;

    this.tableDataAvailable = this.mappedTableValues.length > 0;
  }

  private toChartDataset(
    data: number[],
    label: string | undefined,
    backgroundColor: Color | Color[] | undefined,
    borderColor: Color | Color[] | undefined,
    fill: boolean = false,
  ) {

    return {
      data,
      label,
      backgroundColor,
      borderColor,
      fill,
    };
  }

}
